import type { TFunction } from "i18next";
import { messages } from "../../../../../locales/messages";
import { ReviewChangeRequest } from "../../types";

export const MANUAL_SIGNAL_FIELD_KEYS = new Set([
  "image",
  "swapPosition",
  "deleteItem",
]);

export const isManualSignalField = (fieldKey?: unknown): boolean =>
  fieldKey != null &&
  fieldKey !== "" &&
  MANUAL_SIGNAL_FIELD_KEYS.has(String(fieldKey));

export const manualSignalFieldLabel = (
  fieldKey: unknown,
  t: TFunction,
): string => {
  switch (String(fieldKey)) {
    case "image":
      return t(messages.Review_Manual_Cr_Field_Image());
    case "swapPosition":
      return t(messages.Review_Manual_Cr_Field_Swap_Position());
    case "deleteItem":
      return t(messages.Review_Manual_Cr_Field_Delete_Item());
    default:
      return String(fieldKey);
  }
};

/** Shape of a `deleteItem` change-request value. */
export interface DeleteItemValue {
  reason: string;
}

/** Shape of a `swapPosition` change-request value. */
export interface SwapPositionValue {
  partnerFlyerItemId: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Parse a CR value that may be an object or a JSON-encoded object string. */
const parseObjectValue = (value: unknown): unknown => {
  if (isRecord(value)) return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  return null;
};

export const isDeleteItemValue = (v: unknown): v is DeleteItemValue =>
  isRecord(v) && typeof v.reason === "string" && v.reason.trim().length > 0;

export const isSwapPositionValue = (v: unknown): v is SwapPositionValue =>
  isRecord(v) &&
  typeof v.partnerFlyerItemId === "string" &&
  v.partnerFlyerItemId.trim().length > 0;

export const isImageUrlValue = (value: unknown): value is string =>
  typeof value === "string" && /^https?:\/\//.test(value);

export const parseDeleteItemReason = (value: unknown): string | undefined => {
  const obj = parseObjectValue(value);
  return isDeleteItemValue(obj) ? obj.reason.trim() : undefined;
};

export const parseSwapPartnerId = (value: unknown): string | undefined => {
  const obj = parseObjectValue(value);
  return isSwapPositionValue(obj) ? obj.partnerFlyerItemId.trim() : undefined;
};

export const collectSwapPartnerIds = (
  changeRequests: ReviewChangeRequest[],
): string[] => {
  const ids = new Set<string>();
  changeRequests.forEach((cr) => {
    if (String(cr.fieldKey) !== "swapPosition") return;
    const partnerId = parseSwapPartnerId(cr.newValue);
    if (partnerId) ids.add(partnerId);
  });
  return Array.from(ids);
};

export const collectSwapPartnerIdsFromGroups = (
  groups: { changeRequests: ReviewChangeRequest[] }[],
): string[] => {
  const ids = new Set<string>();
  groups.forEach((g) => {
    collectSwapPartnerIds(g.changeRequests).forEach((id) => ids.add(id));
  });
  return Array.from(ids);
};
